import React, { useState, useEffect, useCallback } from "react";
import { Layers, ShieldCheck, Clock, RefreshCw } from "lucide-react";
import { fetchWithAuth } from "../../utils/api";
import AssetBadge from "../common/AssetBadge";
import EmptyState from "../common/EmptyState";
import { TableSkeleton } from "../common/Skeleton";
import { chainDisplayLabel } from "../../utils/assetLogos";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const fmtUsd = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// Token amounts span 18 decimals for native assets and 2 for stablecoins, so a
// fixed precision either truncates a real balance or pads a tidy one with
// zeroes. Trailing zeroes are dropped instead.
const fmtToken = (v, symbol) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return `— ${symbol || ""}`.trim();
  const digits = n !== 0 && Math.abs(n) < 1 ? 6 : 2;
  const text = n
    .toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits })
    .replace(/\.0+$/, "");
  return `${text} ${symbol || ""}`.trim();
};

// `mixed` is a real state, not a fallback: a merchant can hold USDT on Ethereum
// from a direct payment AND from an auto-conversion, and labelling that as
// either one alone misstates part of the balance.
const SOURCE_LABELS = {
  auto_conversion: { label: "Auto-converted", tone: "converted" },
  direct: { label: "Direct payment", tone: "direct" },
  mixed: { label: "Mixed", tone: "mixed" },
};

/**
 * One fetch of the merchant's balance, owned by whoever lays the page out.
 *
 * The stat cards and the balance table are rendered in different places on the
 * Auto-conversion page, with the settings card between them. Each fetching its
 * own copy would issue the same request twice and let the two disagree while
 * one was still in flight.
 */
export const useMerchantBalances = (isTestnet = false) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // isTestnet is REQUIRED, not optional. The endpoint only filters on
      // is_testnet when the parameter is present — omit it and the response
      // sums testnet and mainnet together, so a Live view reported a testnet
      // balance as real money.
      const params = new URLSearchParams({ isTestnet: isTestnet ? 'true' : 'false' });
      const res = await fetchWithAuth(`${API_BASE_URL}/payouts/my-balance?${params}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load balances");
      setData(json.data);
    } catch (err) {
      console.error("[AutoConversion] balance load failed:", err.message);
      setError(true);
    } finally {
      setLoading(false);
    }
    // Refetches when the merchant switches Test/Live.
  }, [isTestnet]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = Number(data?.pendingPayoutAmount || 0);
  const available = Number(data?.availableBalance || 0);

  return {
    rows: data?.balances || [],
    pending,
    available,
    // What the merchant holds, spendable or not: a reserved payout has not
    // left the account yet.
    total: available + pending,
    loading,
    error,
    reload: load,
  };
};

export const BalanceStatCards = ({ total, available, pending, loading }) => (
  <div className="acb-stats">
        <div className="acb-stat">
          <div className="acb-stat-text">
            <span className="acb-stat-label">Total Balance</span>
            <strong className="acb-stat-value">{loading ? "—" : fmtUsd(total)}</strong>
          </div>
          <span className="acb-stat-icon acb-icon-total">
            <Layers size={18} />
          </span>
        </div>

        <div className="acb-stat">
          <div className="acb-stat-text">
            <span className="acb-stat-label">Available for Payout</span>
            <strong className="acb-stat-value">{loading ? "—" : fmtUsd(available)}</strong>
          </div>
          <span className="acb-stat-icon acb-icon-available">
            <ShieldCheck size={18} />
          </span>
        </div>

    <div className="acb-stat">
      <div className="acb-stat-text">
        <span className="acb-stat-label">Pending Payout</span>
        <strong className="acb-stat-value">{loading ? "—" : fmtUsd(pending)}</strong>
      </div>
      <span className="acb-stat-icon acb-icon-pending">
        <Clock size={18} />
      </span>
    </div>
    <style jsx>{styles}</style>
  </div>
);

export const AssetWiseBalances = ({ rows, loading, error, reload, aside = null }) => {
  return (
    <div className="acb-split">
      <div className="acb-card">
        <div className="acb-card-head">
          <div>
            <h2>Asset-wise Balances</h2>
            <p>Your current balances across all networks and tokens.</p>
          </div>
          <button type="button" className="acb-refresh" onClick={reload} disabled={loading}>
            <RefreshCw size={14} className={loading ? "acb-spin" : undefined} />
            Refresh
          </button>
        </div>

        {loading ? (
          <TableSkeleton rows={3} />
        ) : error ? (
          <div className="acb-error">
            <p>Couldn&apos;t load balances.</p>
            <button type="button" className="acb-refresh" onClick={reload}>
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Layers size={24} />}
            title="No balances yet"
            subtitle="Balances appear here once a payment settles."
          />
        ) : (
          <div className="acb-table-scroll">
            <table className="acb-table">
              <thead>
                <tr>
                  <th>Network</th>
                  <th>Asset</th>
                  <th className="acb-num">Available</th>
                  <th className="acb-num">USD Value</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const source = SOURCE_LABELS[row.source] || SOURCE_LABELS.direct;
                  return (
                    // network + token is the identity: the same symbol on two
                    // chains is two different balances and must not collapse
                    // into one row.
                    <tr key={`${row.networkId ?? row.chain}|${row.token}`}>
                      <td>
                        <span className="acb-chain">{chainDisplayLabel(row.chain) || row.chain}</span>
                      </td>
                      <td>
                        <AssetBadge symbol={row.token} chain={row.chain} />
                      </td>
                      <td className="acb-num acb-amount">{fmtToken(row.available, row.token)}</td>
                      <td className="acb-num">
                        {row.fullyUnpriced ? (
                          <span className="acb-unknown" title="No USD price available for this asset">
                            unavailable
                          </span>
                        ) : (
                          fmtUsd(row.availableUsd)
                        )}
                      </td>
                      <td>
                        <span className={`acb-source acb-source-${source.tone}`}>{source.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {aside}

      <style jsx>{styles}</style>
    </div>
  );
};

const styles = `
  .acb-wrap {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .acb-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .acb-stat {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 20px 22px;
  }
  .acb-stat-text {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
  .acb-stat-label {
    font-size: 13px;
    color: var(--ink-secondary);
  }
  .acb-stat-value {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--ink);
  }
  .acb-stat-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    flex: none;
  }
  .acb-icon-total { background: rgba(109, 93, 246, 0.12); color: var(--brand-600); }
  .acb-icon-available { background: rgba(22, 163, 116, 0.12); color: #16a374; }
  .acb-icon-pending { background: rgba(120, 128, 160, 0.14); color: var(--ink-secondary); }

  /* Balances lead; the configuration history is the narrower supporting panel
     beside them, and stacks underneath once there is no room for two columns. */
  .acb-split {
    display: grid;
    grid-template-columns: minmax(0, 1.9fr) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
  }

  .acb-card {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 22px 24px;
    height: 450px;
    overflow-y: scroll;
  }
  .acb-card-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
  .acb-card-head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
  .acb-card-head p {
    margin: 3px 0 0;
    font-size: 13px;
    color: var(--ink-secondary);
  }
  .acb-refresh {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--bg-subtle);
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
    color: var(--ink-secondary);
    font-size: 12px;
    font-weight: 600;
    padding: 7px 11px;
    cursor: pointer;
  }
  .acb-refresh:hover:not(:disabled) { border-color: var(--line-strong); color: var(--ink); }
  .acb-refresh:disabled { opacity: 0.6; cursor: default; }
  .acb-spin { animation: acb-rotate 1s linear infinite; }
  @keyframes acb-rotate { to { transform: rotate(360deg); } }

  .acb-table-scroll { overflow-x: auto; }
  .acb-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .acb-table th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    padding: 10px 14px;
    background: var(--bg-subtle);
    white-space: nowrap;
  }
  .acb-table th:first-child { border-top-left-radius: var(--r-sm); }
  .acb-table th:last-child { border-top-right-radius: var(--r-sm); text-align:right; }
  .acb-table td {
    padding: 14px;
    border-bottom: 1px solid var(--line);
    color: var(--ink);
    vertical-align: middle;
  }
  .acb-table td:last-child{text-align:right;}
  .acb-table tbody tr:last-child td { border-bottom: none; }
  .acb-num { text-align: right; }
  .acb-table th.acb-num { text-align: right; }
  .acb-amount { font-variant-numeric: tabular-nums; font-weight: 600; }
  .acb-chain { font-weight: 600; }
  .acb-unknown { font-style: italic; color: var(--ink-muted); }

  .acb-source {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .acb-source-converted { background: rgba(109, 93, 246, 0.12); color: var(--brand-700); }
  .acb-source-direct { background: rgba(120, 128, 160, 0.14); color: var(--ink-secondary); }
  .acb-source-mixed { background: rgba(217, 155, 32, 0.15); color: #96690f; }

  .acb-error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 18px 0;
    font-size: 13px;
    color: var(--ink-secondary);
  }
  .acb-error p { margin: 0; }

  @media (max-width: 1180px) {
    .acb-split { grid-template-columns: 1fr; }
  }
  @media (max-width: 900px) {
    .acb-stats { grid-template-columns: 1fr; }
  }
`;

